import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { AlertCircle, Lock, Loader2, Plus, WalletCards } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { PageMessage } from "@/components/page-message";
import { useOrganization } from "@/hooks/use-organization";
import { Permission, useRolePermissions } from "@/hooks/use-role-permissions";
import { useTRPC } from "@/trpc";
import { CreateOrganizationWageSetDialog } from "./create-organization-wage-set-dialog";
import { OrganizationWageSetsList } from "./organization-wage-sets-list";

type OrganizationWageSetsPageProps = {
  user: { is_admin: boolean } | null;
};

export function OrganizationWageSetsPage({
  user,
}: OrganizationWageSetsPageProps) {
  const trpc = useTRPC();
  const { selectedOrgBusinessUnit } = useOrganization();
  const { hasPermission } = useRolePermissions(user);
  const canView = hasPermission(Permission.ViewJobOffers);
  const canManage = hasPermission(Permission.ManageJobOffers);
  const [isCreateOpen, setIsCreateOpen] = useState(false);

  const organizationWageSetsQuery = useQuery(
    trpc.organizationWageSet.list.queryOptions(
      { organizationBusinessUnitId: selectedOrgBusinessUnit.id },
      { enabled: canView },
    ),
  );

  if (!canView) {
    return (
      <PageMessage
        icon={Lock}
        title="No access to wage sets"
        description="You don't have permission to view wage sets for this business unit."
      />
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="mx-auto max-w-4xl space-y-4 px-3 py-4 sm:space-y-6 sm:px-4 sm:py-8 lg:px-8">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="space-y-2">
            <h1 className="text-3xl font-bold text-slate-900">Wage sets</h1>
            <p className="text-sm text-slate-600 sm:text-base">
              Named pay terms for unscheduled work, assigned to hired providers
              or provider pools.
            </p>
          </div>
          {canManage && (
            <Button onClick={() => setIsCreateOpen(true)}>
              <Plus className="mr-2 h-4 w-4" />
              Create wage set
            </Button>
          )}
        </div>

        {organizationWageSetsQuery.isLoading && (
          <div className="flex justify-center py-12">
            <Loader2
              aria-label="Loading wage sets"
              className="h-8 w-8 animate-spin text-blue-600"
            />
          </div>
        )}

        {organizationWageSetsQuery.error && (
          <Card className="border-red-200 bg-red-50">
            <CardContent className="flex flex-wrap items-center gap-3 p-4 text-sm text-red-800">
              <AlertCircle className="h-5 w-5" aria-hidden="true" />
              <span>Couldn't load wage sets.</span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => organizationWageSetsQuery.refetch()}
              >
                Try again
              </Button>
            </CardContent>
          </Card>
        )}

        {organizationWageSetsQuery.data &&
          organizationWageSetsQuery.data.length === 0 && (
            <Card className="border-dashed">
              <CardContent className="flex flex-col items-center p-8 text-center">
                <WalletCards
                  aria-hidden="true"
                  className="mb-3 h-10 w-10 text-slate-300"
                />
                <h2 className="mb-1 font-semibold text-slate-900">
                  No wage sets yet
                </h2>
                <p className="max-w-md text-sm text-slate-600">
                  Create a wage set such as "Clinic visits" to set pay terms
                  once and assign them to providers.
                </p>
              </CardContent>
            </Card>
          )}

        {organizationWageSetsQuery.data &&
          organizationWageSetsQuery.data.length > 0 && (
            <OrganizationWageSetsList
              organizationWageSets={organizationWageSetsQuery.data}
              organizationBusinessUnitId={selectedOrgBusinessUnit.id}
              timezoneId={selectedOrgBusinessUnit.timezoneId}
            />
          )}
      </div>
      {isCreateOpen && (
        <CreateOrganizationWageSetDialog open onOpenChange={setIsCreateOpen} />
      )}
    </div>
  );
}
